import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Save, Upload, Sparkles, X } from "lucide-react";

const empty = {
  title: "", slug: "", category: "Actualités", tags: [], excerpt: "", content: "",
  cover_image_url: "", video_url: "",
  published: true, scheduled_at: "",
  seo_title: "", seo_description: "",
};

export default function BlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiCategory, setAiCategory] = useState("Conseils");
  const [aiStyle, setAiStyle] = useState("professionnel et engageant");
  const [generating, setGenerating] = useState(false);
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    if (id) {
      api.get("/admin/blog").then(r => {
        const p = r.data.find(x => x.id === id);
        if (p) setForm({ ...empty, ...p, tags: p.tags || [], scheduled_at: p.scheduled_at || "" });
      });
    }
  }, [id]);

  const set = (k, v) => setForm({ ...form, [k]: v });
  const slugify = (s) => s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g,"").replace(/[^a-z0-9]+/g,"-").replace(/^-|-$/g,"");

  const generateAI = async () => {
    if (!aiTopic.trim()) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/blog/generate", { topic: aiTopic, category: aiCategory, style: aiStyle });
      setForm({
        ...form,
        title: data.title || form.title,
        slug: data.slug || slugify(data.title || ""),
        category: data.category || aiCategory,
        excerpt: data.excerpt || "",
        content: data.content || "",
        tags: data.tags || [],
        seo_title: data.seo_title || "",
        seo_description: data.seo_description || "",
        published: false,
      });
      toast.success("Article généré ! Relisez et publiez.");
      setAiOpen(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la génération IA");
    } finally { setGenerating(false); }
  };

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_image_url", `${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      toast.success("Image uploadée");
    } catch { toast.error("Échec upload"); }
    finally { setUploading(false); }
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    set("tags", [...form.tags, tagInput.trim()]); setTagInput("");
  };
  const removeTag = (i) => set("tags", form.tags.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form, slug: form.slug || slugify(form.title),
        scheduled_at: form.scheduled_at || null,
        tags: form.tags || [],
      };
      if (id) { await api.put(`/admin/blog/${id}`, payload); toast.success("Article mis à jour"); }
      else { await api.post("/admin/blog", payload); toast.success("Article créé"); }
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-4xl mx-auto px-6 md:px-8 py-12">
        <button onClick={() => navigate("/admin")} className="text-xs font-bold uppercase tracking-widest mb-6 flex items-center gap-2" style={{ color: "var(--text-secondary)" }} data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Retour au tableau de bord
        </button>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">{id ? "Modifier l'article" : "Nouvel article"}</h1>
          {!id && (
            <button onClick={() => setAiOpen(true)} data-testid="ai-generate-btn" className="btn-brutal btn-brutal-solar px-5 py-3 font-bold uppercase tracking-wider text-sm flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Générer avec IA
            </button>
          )}
        </div>

        <form onSubmit={submit} className="bg-[color:var(--bg-surface)] border p-8 md:p-10 space-y-6" style={{ borderColor: "var(--border-default)" }} data-testid="blog-form">
          <Field label="Titre *"><input required value={form.title} onChange={e => set("title", e.target.value)} className="input" data-testid="bf-title" /></Field>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Slug (URL)"><input value={form.slug} onChange={e => set("slug", e.target.value)} className="input font-mono" placeholder={slugify(form.title)} data-testid="bf-slug" /></Field>
            <Field label="Catégorie"><input value={form.category} onChange={e => set("category", e.target.value)} className="input" data-testid="bf-category" /></Field>
          </div>

          <Field label="Tags">
            <div className="flex gap-2 mb-2">
              <input value={tagInput} onChange={e => setTagInput(e.target.value)} onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addTag())} className="input flex-1" placeholder="ex: photovoltaïque" data-testid="bf-tag-input" />
              <button type="button" onClick={addTag} className="btn-brutal bg-[color:var(--bg-surface)] border border-slate-900 px-4 font-bold uppercase tracking-wider text-xs">Ajouter</button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((t, i) => <span key={i} className="tag flex items-center gap-2">{t}<button type="button" onClick={() => removeTag(i)} className="text-red-600"><X className="w-3 h-3"/></button></span>)}
            </div>
          </Field>

          <Field label="Extrait"><textarea rows={2} value={form.excerpt} onChange={e => set("excerpt", e.target.value)} className="input resize-none" data-testid="bf-excerpt" /></Field>
          <Field label="Contenu *"><textarea required rows={14} value={form.content} onChange={e => set("content", e.target.value)} className="input resize-none font-mono text-sm" data-testid="bf-content"
              placeholder="Texte de l'article. Utilisez ## pour les sous-titres et - pour les listes. Markdown basique supporté." /></Field>

          <div className="grid md:grid-cols-[1fr_auto] gap-3">
            <Field label="Image de couverture (URL)"><input value={form.cover_image_url} onChange={e => set("cover_image_url", e.target.value)} className="input" data-testid="bf-cover-url" /></Field>
            <div className="flex flex-col justify-end">
              <label className="btn-brutal bg-[color:var(--bg-surface)] border border-slate-900 px-4 py-3 text-xs font-bold uppercase tracking-wider cursor-pointer flex items-center gap-2" data-testid="bf-upload-cover">
                <Upload className="w-3 h-3"/>{uploading ? "Upload..." : "Uploader"}
                <input type="file" accept="image/*" onChange={e => e.target.files[0] && uploadCover(e.target.files[0])} className="hidden" />
              </label>
            </div>
          </div>
          <Field label="URL vidéo (YouTube/Vimeo, optionnel)"><input value={form.video_url} onChange={e => set("video_url", e.target.value)} className="input" data-testid="bf-video-url" /></Field>

          <div className="border-t pt-6" style={{ borderColor: "var(--border-default)" }}>
            <div className="text-xs font-bold uppercase tracking-widest mb-4" style={{ color: "var(--text-secondary)" }}>Publication & SEO</div>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="SEO Title"><input value={form.seo_title} onChange={e => set("seo_title", e.target.value)} className="input" data-testid="bf-seo-title" /></Field>
              <Field label="Date de publication programmée (optionnel)"><input type="datetime-local" value={form.scheduled_at?.slice(0,16) || ""} onChange={e => set("scheduled_at", e.target.value ? new Date(e.target.value).toISOString() : "")} className="input" data-testid="bf-scheduled" /></Field>
            </div>
            <Field label="SEO Description"><textarea rows={2} value={form.seo_description} onChange={e => set("seo_description", e.target.value)} className="input resize-none mt-3" data-testid="bf-seo-desc" /></Field>
          </div>

          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={form.published} onChange={e => set("published", e.target.checked)} className="w-4 h-4 accent-slate-900" data-testid="bf-published" />
            <span className="text-sm font-semibold">Publier l'article {form.scheduled_at && <span className="text-xs opacity-60">(à la date programmée)</span>}</span>
          </label>

          <button disabled={saving} type="submit" data-testid="bf-submit"
            className="btn-brutal btn-brutal-solar w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-3 disabled:opacity-50">
            {saving ? "Enregistrement..." : (id ? "Mettre à jour" : "Créer l'article")} {!saving && <Save className="w-4 h-4" />}
          </button>
        </form>
      </section>
      <Footer />

      {aiOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-6" onClick={() => setAiOpen(false)}>
          <div onClick={e => e.stopPropagation()} className="bg-[color:var(--bg-surface)] border border-slate-900 w-full max-w-lg p-8 space-y-4" data-testid="ai-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-display font-bold text-xl flex items-center gap-2"><Sparkles className="w-5 h-5 text-[color:var(--solar)]"/>Générer un article avec IA</h2>
              <button onClick={() => setAiOpen(false)}><X className="w-5 h-5"/></button>
            </div>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>Claude Sonnet 4.5 — l'IA rédige titre, contenu, SEO, tags et FAQ. Vous relisez avant publication.</p>
            <Field label="Sujet de l'article *">
              <textarea required rows={2} value={aiTopic} onChange={e => setAiTopic(e.target.value)} className="input resize-none" placeholder='ex: "Rentabilité d un hangar photovoltaïque en 2026"' data-testid="ai-topic"/>
            </Field>
            <Field label="Catégorie">
              <select value={aiCategory} onChange={e => setAiCategory(e.target.value)} className="input" data-testid="ai-category">
                {["Actualités","Conseils","Investissement","Photovoltaïque","Immobilier","Études de cas"].map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Style">
              <select value={aiStyle} onChange={e => setAiStyle(e.target.value)} className="input">
                <option>professionnel et engageant</option>
                <option>informatif et pédagogique</option>
                <option>commercial et orienté conversion</option>
                <option>technique et précis</option>
              </select>
            </Field>
            <button onClick={generateAI} disabled={generating || !aiTopic.trim()} data-testid="ai-generate-submit"
              className="btn-brutal btn-brutal-solar w-full px-6 py-3 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 disabled:opacity-50">
              {generating ? "Génération... (~30s)" : "Générer l'article"} {!generating && <Sparkles className="w-4 h-4"/>}
            </button>
            <p className="text-xs text-center" style={{ color: "var(--text-muted)" }}>Décompte sur votre clé Universal Emergent.</p>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: "var(--text-secondary)" }}>{label}</span>
    {children}
  </label>
);
